import React, { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, TrendingUp, Info, Briefcase } from 'lucide-react';
import { liveAnnouncementsAPI, getErrorMessage } from '../../lib/api';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';

const MyStocksContent = () => {
  const itemsPerPage = 20;
  
  // State
  const [accessibleStocks, setAccessibleStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [selectedStocksCount, setSelectedStocksCount] = useState(0);
  const [selectedIndexesCount, setSelectedIndexesCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user selections and accessible stocks
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await liveAnnouncementsAPI.getUserSelections();
      const data = response.data?.data || response.data || {};
      
      setAccessibleStocks(data.total_accessible_stocks_list || []);
      setFilteredStocks(data.total_accessible_stocks_list || []);
      setSelectedStocksCount(data.selected_stocks_count || 0);
      setSelectedIndexesCount(data.selected_indexes_count || 0);
    } catch (err) {
      console.error('Failed to fetch accessible stocks:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter stocks when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredStocks(accessibleStocks);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = accessibleStocks.filter(stock => 
        stock.name?.toLowerCase().includes(query) ||
        stock.symbol?.toLowerCase().includes(query) ||
        String(stock.scrip_code).includes(query)
      );
      setFilteredStocks(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, accessibleStocks]);

  // Pagination calculations
  const totalStocks = filteredStocks.length;
  const totalPages = Math.ceil(totalStocks / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalStocks);
  const paginatedStocks = filteredStocks.slice(startIndex, endIndex);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading your stocks...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load stocks</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (accessibleStocks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Briefcase className="mx-auto text-gray-300 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Stocks Selected</h2>
          <p className="text-gray-500 mb-6">
            You haven't selected any stocks or indexes yet. Start by selecting stocks from Stock Search or indexes from Index Search.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="/dashboard/stocks"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Go to Stock Search
            </a>
            <a
              href="/dashboard/index-search"
              className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
            >
              Go to Index Search
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Accessible Stocks</p>
              <p className="text-3xl font-bold mt-1">{accessibleStocks.length}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <TrendingUp size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Directly Selected Stocks</p>
              <p className="text-3xl font-bold mt-1">{selectedStocksCount}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Briefcase size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Selected Indexes</p>
              <p className="text-3xl font-bold mt-1">{selectedIndexesCount}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <TrendingUp size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900">About My Stocks</h3>
            <p className="text-sm text-blue-800 mt-1">
              This page shows all stocks you can track for live announcements. It includes stocks you've directly selected 
              plus all constituent stocks from your selected indexes. Live Corporate Announcements are filtered based on this list.
            </p>
          </div>
        </div>
      </div>

      {/* Search & Table Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Search Header */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="text-blue-600" size={24} />
              My Accessible Stocks
              <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                {totalStocks} {searchQuery && totalStocks !== accessibleStocks.length ? `of ${accessibleStocks.length}` : ''} Stocks
              </span>
            </h2>
            
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, symbol or scrip code..."
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Results Info */}
        {searchQuery && (
          <div className="bg-gray-50 px-6 py-3 border-b">
            <p className="text-sm text-gray-600">
              Showing {paginatedStocks.length} results for "<span className="font-semibold">{searchQuery}</span>"
              {filteredStocks.length !== accessibleStocks.length && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="ml-2 text-blue-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </p>
          </div>
        )}

        {/* Table */}
        {paginatedStocks.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">S.NO.</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">STOCK NAME</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">SCRIP CODE</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">SYMBOL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedStocks.map((stock, idx) => (
                    <tr key={stock.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-gray-900">
                        {startIndex + idx + 1}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-900 font-medium">{stock.name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {stock.scrip_code || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm text-blue-600 font-semibold">{stock.symbol || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {endIndex} of {totalStocks} stocks
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-4 py-2 rounded-lg ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <Search className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">No stocks found</p>
            <p className="text-gray-400 text-sm">Try adjusting your search query</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Wrapper component with access control
const MyStocks = () => {
  return (
    <UpgradeOverlay service="stock_selection">
      <MyStocksContent />
    </UpgradeOverlay>
  );
};

export default MyStocks;
